import { makeAutoObservable } from 'mobx'
import { type IResponseFilter } from 'entities/Contacts/api/filterTypes'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { FilterItem } from 'widgets/FilterEditor/model/FilterItem'
import type { FilterGroups } from 'widgets/FilterEditor/model/FilterGroups'
import type { FiltersConfig } from 'widgets/FilterEditor/model/FiltersConfig'
import { FilterItemConfig } from 'widgets/FilterEditor/model/FilterItemConfig.ts'

export class FilterGroup {
  private _filters = new Array<FilterItem>()

  constructor(
    private _parent: FilterGroups,
    private _config: FiltersConfig,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  get filters() {
    return Array.from(this._filters)
  }

  get firstFilter(): FilterItem | null {
    return this._filters[0] ?? null
  }

  get lastFilter(): FilterItem | null {
    return this._filters[this._filters.length - 1] ?? null
  }

  get count() {
    return this._filters.length
  }

  get countWithValue() {
    return this._filters.filter((filter) => !filter.isEmpty).length
  }

  get countWithOutValue() {
    return this._filters.filter((filter) => filter.isEmpty).length
  }

  get isEmpty() {
    return this._filters.length === 0
  }

  get isFirst() {
    const index = this._parent.groups.indexOf(this)

    return index === 0
  }

  get isLast() {
    const index = this._parent.groups.indexOf(this)

    return this._parent.groups.length - index === 1
  }

  createFilter(key: string, silent = false) {
    const config = this._config.getAnyFilter(key)
    if (!config) return this._parent.clearSpawnedFilter()

    const operators = this._config.getOperators(config.type) ?? []
    const item = { key, type: config.type, operator: operators[0]?.key ?? null }
    const filter = new FilterItem(item, operators, config)

    filter.setPlaceholderPrefix(this._parent.placeholderPrefixMap)

    if (!silent) this._parent.setSpawnedFilter(filter)

    this._filters.push(filter)

    return filter
  }

  // TODO: Temporary solution must to be removed/updated after implementing filter on the backend side (as other filters)
  async createLocalFilter(data: Record<string, unknown>, silent = false) {
    const item = {
      key: data.key as string,
      type: data.type as string,
      operator: data.operator as string,
    }

    try {
      const { data: broadcast } = await this._entities.broadcastStore.getItemById(
        data.source_id as string
      )

      const config: IResponseFilter = {
        category: '',
        category_new: '',
        editable: false,
        is_values_required: false,
        key: item.key,
        name: `Broadcast: ${broadcast?.name}`,
        type: 'local',
        visible: true,
      }

      const filter = new FilterItem(item, [], new FilterItemConfig(config))

      filter.setPlaceholderPrefix(this._parent.placeholderPrefixMap)

      if (!silent) this._parent.setSpawnedFilter(filter)

      this._filters.push(filter)
      return filter
    } catch (e) {
      console.error(e)
    }
  }

  removeFilter(filter: FilterItem) {
    filter.dispose()
    this._filters = this._filters.filter((current) => current !== filter)
  }

  delete() {
    this._parent.removeGroup(this)
  }

  clear() {
    this._filters = []
  }

  populateJson(data: Record<string, unknown>[]) {
    data.map((item) => {
      if (typeof item.key !== 'string') return

      // TODO: Temporary solution must to be removed/updated after implementing filter on the backend side (as other filters)
      if (item.type === 'broadcast') {
        this.createLocalFilter(item, true).then((data) => data?.populateJson(item))

        return
      }

      this.createFilter(item.key, true)?.populateJson(item)
    })
  }

  get isLocal() {
    // TODO: Temporary solution must to be removed/updated after implementing filter on the backend side (as other filters)
    return this._filters.filter((filter) => filter.type === 'broadcast').length
  }
}
